declare global {
    interface Window {
        dataLayer?: Record<string, unknown>[];
        gtag?: (...args: unknown[]) => void;
        pushToGoogleTagManager?: (eventData: Record<string, unknown>) => boolean;
    }
}

type GtmEvent = Record<string, unknown>;
type PushOptions = { waitForGtmLoad?: boolean };

const GTM_POLL_INTERVAL_MS = 50;

let isGtmReady = false;
const pendingGtmEvents: GtmEvent[] = [];
let pollInterval: ReturnType<typeof setInterval> | null = null;

export function getDataLayer(): Record<string, unknown>[] | null {
    if (typeof window === 'undefined') {
        return null;
    }

    window.dataLayer ??= [];
    return window.dataLayer;
}

function hasGtmLoad(dataLayer: Record<string, unknown>[]): boolean {
    return dataLayer.some((entry) => entry.event === 'gtm.load');
}

function pushToDataLayer(event: GtmEvent): boolean {
    const dataLayer = getDataLayer();

    if (!dataLayer) {
        return false;
    }

    try {
        dataLayer.push(event);
        return true;
    } catch {
        return false;
    }
}

function stopPolling(): void {
    if (pollInterval !== null) {
        clearInterval(pollInterval);
        pollInterval = null;
    }
}

function pushPendingGtmEvents(): void {
    if (isGtmReady) {
        return;
    }

    isGtmReady = true;
    stopPolling();

    const dataLayer = getDataLayer();

    if (!dataLayer) {
        return;
    }

    while (pendingGtmEvents.length > 0) {
        const gtmEvent = pendingGtmEvents.shift()!;

        try {
            dataLayer.push(gtmEvent);
        } catch {
            // Keep pushing remaining queued events.
        }
    }
}

function startPolling(): void {
    if (pollInterval !== null || isGtmReady) {
        return;
    }

    pollInterval = setInterval(() => {
        const dataLayer = getDataLayer();

        if (dataLayer && hasGtmLoad(dataLayer)) {
            pushPendingGtmEvents();
        }
    }, GTM_POLL_INTERVAL_MS);
}

export function resetGoogleTagManagerStateForTests() {
    isGtmReady = false;
    stopPolling();
    pendingGtmEvents.length = 0;
}

export function pushToGoogleTagManager(
    gtmEvent: GtmEvent | null | undefined,
    { waitForGtmLoad = true }: PushOptions = {}
): boolean {
    if (!gtmEvent) {
        return false;
    }

    const dataLayer = getDataLayer();

    if (!dataLayer) {
        return false;
    }

    if (!waitForGtmLoad) {
        return pushToDataLayer(gtmEvent);
    }

    if (isGtmReady || hasGtmLoad(dataLayer)) {
        if (!isGtmReady && hasGtmLoad(dataLayer)) {
            pushPendingGtmEvents();
        }

        return pushToDataLayer(gtmEvent);
    }

    pendingGtmEvents.push(gtmEvent);
    startPolling();

    return true;
}
